import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useOverlayWS } from "../lib/useOverlayWS";
import { LivePanel } from "../components/studio/LivePanel";
import { FontFaceStyle } from "../components/FontFaceStyle";
import { Headphones, Loader2, Eye, EyeOff } from "lucide-react";
import { t, tx } from "../i18n";
import { LangSwitcher } from "../components/LangSwitcher";

export default function Operator() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const key = params.get("key");
  const [overlay, setOverlay] = useState(null);
  const [elements, setElements] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState(0);
  const [ytData, setYtData] = useState(null);
  const [twitchData, setTwitchData] = useState(null);
  const [chatEnabled, setChatEnabled] = useState(false);
  const [chatDemo, setChatDemo] = useState(false);
  const [chatSource, setChatSource] = useState(null);
  const [chatConn, setChatConn] = useState({ youtube: false, twitch: false });
  const [chatMessages, setChatMessages] = useState([]);
  const [outputToken, setOutputToken] = useState(null);
  const [showPreview, setShowPreview] = useState(() => localStorage.getItem("op_preview_hidden") !== "1");
  const [previewRatio, setPreviewRatio] = useState(() => {
    const v = parseFloat(localStorage.getItem("op_preview_ratio"));
    return Number.isFinite(v) ? Math.min(75, Math.max(25, v)) : 55;
  });
  const containerRef = useRef(null);

  const startResize = (e) => {
    e.preventDefault();
    const move = (ev) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || !rect.width) return;
      const pct = Math.min(75, Math.max(25, ((ev.clientX - rect.left) / rect.width) * 100));
      setPreviewRatio(pct);
      localStorage.setItem("op_preview_ratio", String(pct));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const [chatPinned, setChatPinned] = useState(null);
  // PREVIEW (seleção antes de ir ao ar) x NO AR (PGM). A seleção vive só neste navegador.
  const [pvMode, setPvMode] = useState("ar");
  const [pvSel, setPvSel] = useState([]);
  const elementsRef = useRef([]);
  elementsRef.current = elements;

  const loadOperate = useCallback(() => {
    api.get(`/overlays/${id}/operate`, { params: { key } })
      .then(({ data }) => {
        setOverlay(data.overlay);
        setElements(data.overlay.elements || []);
        setFonts(data.fonts || []);
        setChatEnabled(!!data.chat?.enabled);
        setChatDemo(!!data.chat?.demo);
        setChatSource(data.chat?.source || null);
        setChatConn({ youtube: !!data.youtube?.connected, twitch: !!data.twitch?.connected });
        setOutputToken(data.output_token || null);
        if (data.data) setYtData(data.data);
        if (data.twitch_data) setTwitchData(data.twitch_data);
      })
      .catch(() => setError(t("Link de operador inválido ou expirado.")));
  }, [id, key]);

  const { connected, send } = useOverlayWS(id, "operator", key, (msg) => {
    if (msg.type === "sources") setSources(msg.count);
    else if (msg.type === "youtube_data") setYtData(msg.data);
    else if (msg.type === "twitch_data") setTwitchData(msg.data);
    else if (msg.type === "update_element") setElements((prev) => prev.map((e) => (e.id === msg.elementId ? { ...e, ...msg.patch } : e)));
    else if (msg.type === "state") setElements(msg.elements || []);
    else if (msg.type === "hide_all") setElements((prev) => prev.map((e) => ({ ...e, visivel: false })));
    else if (msg.type === "chat_status") { setChatEnabled(msg.enabled); if (msg.demo !== undefined) setChatDemo(msg.demo); if (msg.source !== undefined) setChatSource(msg.source); if (!msg.enabled) setChatMessages([]); }
    else if (msg.type === "chat_state") { setChatMessages(msg.messages || []); setChatPinned(msg.pinned || null); }
    else if (msg.type === "chat_messages") {
      setChatMessages((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        const add = (msg.messages || []).filter((m) => !ids.has(m.id));
        return [...prev, ...add].slice(-60);
      });
    }
    else if (msg.type === "chat_reject") setChatMessages((prev) => prev.filter((x) => x.id !== msg.messageId));
    else if (msg.type === "chat_pin") setChatPinned(msg.message || null);
    else if (msg.type === "chat_error") toast.error(tx(msg.detail) || t("Erro no chat"));
  }, loadOperate);

  useEffect(() => { loadOperate(); }, [loadOperate]);

  const updateElement = (elId, patch) => {
    setElements((prev) => prev.map((e) => (e.id === elId ? { ...e, ...patch } : e)));
    send({ type: "update_element", elementId: elId, patch });
  };
  const hideAll = () => {
    setElements((prev) => prev.map((e) => ({ ...e, visivel: false })));
    send({ type: "hide_all" });
  };
  const toggleGroup = (grupo, visible) => {
    setElements((prev) => prev.map((e) => (e.grupo === grupo ? { ...e, visivel: visible } : e)));
    elementsRef.current.forEach((e) => { if (e.grupo === grupo) send({ type: "update_element", elementId: e.id, patch: { visivel: visible } }); });
  };

  const startChat = async (demo, source) => {
    try { await api.post(`/overlays/${id}/operate/chat/start`, { demo, source }, { params: { key } }); }
    catch (e) { toast.error(e.response?.data?.detail || t("Falha ao iniciar chat")); }
  };
  const stopChat = async () => { try { await api.post(`/overlays/${id}/operate/chat/stop`, {}, { params: { key } }); } catch {} };
  const pinMsg = (m) => { setChatPinned(m); send({ type: "chat_pin", message: m }); };
  const unpinMsg = () => { setChatPinned(null); send({ type: "chat_pin", message: null }); };
  const rejectMsg = (mid) => { setChatMessages((prev) => prev.filter((x) => x.id !== mid)); send({ type: "chat_reject", messageId: mid }); };
  const alertMsg = (m) => { send({ type: "chat_alert", message: m }); toast.success(t("Alerta enviado à tela")); };

  if (error) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-background text-foreground gap-3" data-testid="operator-error">
        <Headphones className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-muted-foreground text-sm">{error}</p>
      </div>
    );
  }
  if (!overlay) {
    return <div className="h-screen flex items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden" data-testid="operator-page">
      <FontFaceStyle fonts={fonts} />
      <header className="h-14 border-b border-border/60 bg-card flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-2 font-display font-bold text-sm min-w-0">
          <img src="/logo-mark.png" alt="LiveGrafX" className="h-6 w-auto flex-shrink-0" />
          <span className="truncate">{overlay.nome}</span>
          <span className="text-[10px] font-mono tracking-widest text-muted-foreground bg-secondary rounded-sm px-1.5 py-0.5 ml-1">{t("OPERADOR")}</span>
        </div>
        <div className="flex items-center gap-2">
        <LangSwitcher />
        {outputToken && (
          <button
            onClick={() => {
              const next = !showPreview;
              setShowPreview(next);
              localStorage.setItem("op_preview_hidden", next ? "0" : "1");
            }}
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground border border-border/60 rounded-sm px-2 py-1"
            data-testid="operator-preview-toggle"
          >
            {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showPreview ? t("Ocultar preview") : t("Mostrar preview")}
          </button>
        )}
        </div>
      </header>
      <div ref={containerRef} className="flex-1 min-h-0 flex flex-col md:flex-row">
        {outputToken && showPreview && (
          <>
            <div
              className="flex-shrink-0 w-full md:w-[var(--pw)] md:h-full bg-black/40 border-b md:border-b-0 md:border-r-0 border-border/60 p-4 flex items-center justify-center overflow-hidden"
              style={{ "--pw": `${previewRatio}%` }}
              data-testid="operator-preview"
            >
              <div
                className="w-full"
                style={{ maxWidth: `min(100%, calc((100vh - 160px) * ${(overlay.largura || 1920) / (overlay.altura || 1080)}))` }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5" data-testid="operator-pgm-tabs">
                    <button
                      onClick={() => setPvMode("preview")}
                      className={`text-[10px] font-mono tracking-[0.2em] px-2.5 py-1 rounded-sm border flex items-center gap-1.5 ${pvMode === "preview" ? "bg-emerald-500/20 border-emerald-500 text-emerald-300" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                      data-testid="operator-tab-preview"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t("PREVIEW")}
                    </button>
                    <button
                      onClick={() => setPvMode("ar")}
                      className={`text-[10px] font-mono tracking-[0.2em] px-2.5 py-1 rounded-sm border flex items-center gap-1.5 ${pvMode === "ar" ? "bg-red-500/20 border-red-500 text-red-300" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                      data-testid="operator-tab-ar"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500 live-dot" /> {t("NO AR")}
                    </button>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {pvMode === "preview" ? t("{n} selecionado(s)", { n: pvSel.length }) : "PGM"}
                  </span>
                </div>
                <div
                  className="relative w-full checker-bg rounded-sm overflow-hidden border border-border/60"
                  style={{ aspectRatio: `${overlay.largura || 1920} / ${overlay.altura || 1080}` }}
                >
                  <iframe
                    src={`/live/${id}?key=${outputToken}${pvMode === "preview" ? `&pv=${encodeURIComponent(pvSel.join(","))}` : ""}`}
                    title={t("Preview do overlay no ar")}
                    className="absolute inset-0 w-full h-full border-0 pointer-events-none"
                    data-testid="operator-preview-iframe"
                  />
                </div>
                <p className="hidden md:block text-[10px] text-muted-foreground mt-1.5 text-center">{t("Arraste a barra ao lado para ajustar o tamanho.")}</p>
              </div>
            </div>
            <div
              className="hidden md:block w-1.5 flex-shrink-0 cursor-col-resize bg-border/60 hover:bg-primary active:bg-primary transition-colors"
              onPointerDown={startResize}
              title={t("Arraste para ajustar o tamanho")}
              data-testid="operator-splitter"
            />
          </>
        )}
        <div className={`flex-1 min-h-0 min-w-0 bg-card ${outputToken && showPreview ? "" : "max-w-xl w-full mx-auto border-x border-border/60"}`}>
          <LivePanel
          elements={elements}
          connected={connected}
          sources={sources}
          ytData={ytData}
          twitchData={twitchData}
          chatEnabled={chatEnabled}
          chatDemo={chatDemo}
          chatSource={chatSource}
          chatConn={chatConn}
          chatMessages={chatMessages}
          chatPinned={chatPinned}
          onChatStart={startChat}
          onChatStop={stopChat}
          onChatPin={pinMsg}
          onChatUnpin={unpinMsg}
          onChatReject={rejectMsg}
          onChatAlert={alertMsg}
          onToggle={(elId, v) => updateElement(elId, { visivel: v })}
          onChangeText={(elId, text) => updateElement(elId, { conteudo: text })}
          onUpdate={updateElement}
          onToggleGroup={toggleGroup}
          pvSel={pvSel}
          onPvToggle={(elId, on) => setPvSel((a) => (on ? [...new Set([...a, elId])] : a.filter((x) => x !== elId)))}
          onTrigger={(elId) => send({ type: "trigger", elementId: elId })}
          onHideAll={hideAll}
        />
        </div>
      </div>
    </div>
  );
}
